<div class="flex h-full w-full flex-col">
  <app-toolbox
    [sections]="toolboxSections()"
    [searchConfig]="searchCfg"
    [searchPlaceholder]="'common.search_placeholder' | translate"
    (searchTermChange)="onSearchTermChange($event)"
    (dropdownSearchChange)="onDropdownSearchChange($event)" />

  <div class="flex h-full flex-col gap-3 overflow-hidden p-3">
    <app-plan-edit-banner class="empty:hidden" [planId]="planId()" />
    <app-plan-edit-bar class="empty:hidden" [planId]="planId()" />

    <!-- Plan check findings -->
    @if (
      findingsLive() ||
      findingsBusy() ||
      findings().length > 0 ||
      planNeverChecked() ||
      findingsAreStale() ||
      findingsPhase() === 'FAILED' ||
      findingsPhase() === 'UNAVAILABLE'
    ) {
      <app-plan-findings-panel
        [findings]="findings()"
        [entityTypeLabels]="entityTypeLabels()"
        [loading]="!findingsLive() && findingsBusy()"
        [planNeverChecked]="!findingsLive() && planNeverChecked()"
        [findingsAreStale]="!findingsLive() && findingsAreStale()"
        [checkFailed]="!findingsLive() && findingsPhase() === 'FAILED'"
        [unavailable]="!findingsLive() && findingsPhase() === 'UNAVAILABLE'"
        [live]="findingsLive()"
        (checkRequested)="requestCheck()" />
    }

    <!-- Deploy status warnings (non-blocking) -->
    @if (deployWarnings().length > 0) {
      <div
        class="shrink-0 overflow-hidden rounded border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20">
        <!-- Header row -->
        <div class="flex flex-wrap items-center gap-3 px-3.5 py-2.5">
          <!-- Icon badge -->
          <div
            class="flex h-6.5 w-6.5 shrink-0 items-center justify-center rounded-md bg-amber-500 text-white">
            <i [class]="ICONS.warning + ' text-sm'"></i>
          </div>

          <!-- Title -->
          <div class="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2.5 gap-y-1">
            <span class="text-sm font-semibold text-amber-700 dark:text-amber-300">
              {{
                'features.plan.deploy.warnings_count'
                  | translate: { count: deployWarnings().length }
              }}
            </span>
          </div>

          <!-- Expand / collapse -->
          <div class="inline-flex gap-1.5">
            <app-button
              [icon]="deployWarningsPanelExpanded() ? ICONS.caretUp : ICONS.caretDown"
              [label]="
                deployWarningsPanelExpanded()
                  ? ('common.collapse' | translate)
                  : ('common.expand' | translate)
              "
              [type]="'cancel'"
              (clickEvent)="
                deployWarningsPanelExpanded.set(!deployWarningsPanelExpanded())
              ">
            </app-button>
          </div>
        </div>

        <!-- Expanded channel list -->
        @if (deployWarningsPanelExpanded()) {
          <div
            class="bg-elevated overflow-y-auto border-t border-amber-300 dark:border-amber-800"
            style="max-height: 18rem">
            @for (channel of deployWarnings(); track channel.configId) {
              <div
                class="border-weak flex flex-col gap-2 border-b px-3.5 py-3 last:border-b-0">
                <!-- Channel name + status badge -->
                <div class="flex flex-wrap items-center gap-2">
                  <span
                    class="text-text-primary min-w-0 flex-1 truncate text-sm font-semibold">
                    {{ channel.name }}
                  </span>
                  <span
                    class="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10.5px] font-bold tracking-wider text-amber-700 uppercase dark:bg-amber-900/40 dark:text-amber-300">
                    {{ channel.status }}
                  </span>
                </div>

                <!-- Warnings -->
                <div class="flex flex-col gap-1">
                  <span class="text-text-secondary text-xs font-medium">
                    {{ 'features.plan.deploy.warnings_label' | translate }}
                  </span>
                  <app-deploy-warnings-list [messages]="channel.warnings" [dense]="true" />
                </div>
              </div>
            }
          </div>
        }
      </div>
    }

    @if (planMetadataVisu(); as metadata) {
      <div class="flex shrink-0 flex-row gap-3">
        <!-- Name -->
        <div
          [class]="
            'relative flex flex-1 flex-col rounded border p-3' +
            (planIsDeployed()
              ? ' border-purple-500 bg-purple-50 dark:bg-purple-900/20'
              : ' border-default bg-elevated')
          ">
          @if (planIsDeployed()) {
            <div class="absolute top-2 right-2 flex items-center gap-1">
              <i class="ph ph-check-circle text-purple-600 dark:text-purple-400"></i>
            </div>
          }
          <h4 class="text-text-secondary mb-1 text-sm font-medium">
            {{ 'features.plan.info' | translate }}
          </h4>
          <p class="text-text-primary text-lg font-semibold">
            {{ metadata.name }}
          </p>
          <!-- Description -->
          <div class="mt-1">
            <!-- <h4 class="mb-1 text-sm font-medium text-text-secondary"> -->
            <!--   Description -->
            <!-- </h4> -->
            <p class="text-text-primary text-sm">
              {{ planDescription() || ('features.plan.no_description' | translate) }}
            </p>
          </div>

          <div class="mt-3 w-full border-t border-dashed border-zinc-300"></div>

          <!-- <h4 class="mb-1 text-sm font-medium text-text-secondary mt-3"> -->
          <!--   Plan Info -->
          <!-- </h4> -->

          <div class="mt-3 space-y-1">
            @if (metadata.planningSpace.name) {
              <div class="flex items-center gap-2">
                <span class="text-text-tertiary text-xs"
                  >{{ 'features.plan.planning_space' | translate }}:
                </span>
                <span class="text-text-primary truncate text-xs">
                  {{ metadata.planningSpace.name }}
                </span>
              </div>
            }
            <div class="flex items-center gap-2">
              <span class="text-text-tertiary text-xs"
                >{{ 'features.plan.trigger' | translate }}:
              </span>
              <span
                class="inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium"
                [class]="
                  metadata.creationTrigger === 'Initial'
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                    : metadata.creationTrigger === 'Optimize'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                      : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300'
                ">
                {{ metadata.creationTrigger }}
              </span>
            </div>
            <div class="flex items-center gap-2" data-testid="plan-findings-status">
              <span class="text-text-tertiary text-xs"
                >{{ 'common.status' | translate }}:
              </span>
              @let status = findingsStatus();
              @if (status.kind === 'busy') {
                <div class="flex items-center gap-2" data-testid="findings-busy">
                  <i
                    [class]="ICONS.arrowsClockwise"
                    class="text-text-tertiary animate-spin text-sm"></i>
                  <span class="text-text-tertiary text-sm">
                    {{ 'common.loading' | translate }}
                  </span>
                </div>
              } @else if (status.kind === 'unavailable') {
                <div class="flex items-center gap-2">
                  <i [class]="ICONS.info" class="text-text-tertiary"></i>
                  <span class="text-text-tertiary text-sm">
                    {{ 'features.plan.findings.unavailable' | translate }}
                  </span>
                </div>
              } @else if (status.kind === 'never_checked') {
                <div class="flex items-center gap-2">
                  <i [class]="ICONS.info" class="text-text-tertiary"></i>
                  <span class="text-text-tertiary text-sm">
                    {{ 'features.plan.findings.never_checked' | translate }}
                  </span>
                </div>
              } @else if (status.kind === 'blocking') {
                <div class="flex items-center gap-2">
                  <i [class]="ICONS.prohibit" class="text-danger"></i>
                  <span class="text-danger text-sm">
                    {{
                      'features.plan.findings.blocking_label'
                        | translate: { count: status.count }
                    }}
                  </span>
                </div>
              } @else if (status.kind === 'warnings') {
                <div class="flex items-center gap-2">
                  <i [class]="ICONS.warning" class="text-warning"></i>
                  <span class="text-warning text-sm">
                    {{
                      'features.plan.findings.warning_label'
                        | translate: { count: status.count }
                    }}
                  </span>
                </div>
              } @else if (status.kind === 'stale') {
                <div class="flex items-center gap-2">
                  <i [class]="ICONS.info" class="text-text-tertiary"></i>
                  <span class="text-text-secondary text-sm">
                    {{ 'features.plan.findings.stale_title' | translate }}
                  </span>
                </div>
              } @else {
                <div class="flex items-center gap-2">
                  <i [class]="ICONS.checkCircle" class="text-success"></i>
                  <span class="text-success text-sm">
                    {{ 'features.plan.no_warnings' | translate }}
                  </span>
                </div>
              }
              @if (status.stale) {
                <span
                  class="bg-info h-2 w-2 shrink-0 rounded-full"
                  aria-hidden="true"></span>
              }
            </div>
            @if (overallDeployStatus() !== 'NONE') {
              <div class="flex items-center gap-2">
                <span class="text-text-tertiary text-xs"
                  >{{ 'features.plan.deploy.status_detail' | translate }}:
                </span>
                <span #deployStatusRef class="flex cursor-default items-center gap-2">
                  @switch (overallDeployStatus()) {
                    @case ('SUCCESS') {
                      <i
                        class="ph-bold ph-check-circle text-purple-600 dark:text-purple-400"></i>
                      <span class="text-sm text-purple-700 dark:text-purple-300">
                        {{ 'features.plan.deploy.status.deployed' | translate }}
                      </span>
                    }
                    @case ('ERROR') {
                      <i class="ph-bold ph-x-circle text-red-600 dark:text-red-400"></i>
                      <span class="text-sm text-red-700 dark:text-red-300">
                        {{ 'features.plan.deploy.status.error' | translate }}
                      </span>
                    }
                    @case ('PROCESSING') {
                      <i class="ph-bold ph-clock text-blue-600 dark:text-blue-400"></i>
                      <span class="text-sm text-blue-700 dark:text-blue-300">
                        {{ 'features.plan.deploy.status.deploying' | translate }}
                      </span>
                    }
                  }
                </span>
                <!-- The deploy page owns everything a failed deploy has to say. -->
                @if (overallDeployStatus() === 'ERROR') {
                  <button
                    type="button"
                    data-testid="deploy-details-link"
                    class="text-accent-main hover:text-accent-hover flex cursor-pointer items-center gap-1 text-xs hover:underline"
                    (click)="goToDeployPage()">
                    {{ 'features.plan.deploy.see_details' | translate }}
                    <i [class]="ICONS.arrowSquareOut"></i>
                  </button>
                }
                <app-tooltip
                  [target]="deployStatusRef"
                  [title]="'features.plan.deploy.status_detail' | translate"
                  [content]="deployTooltipContent()"
                  [position]="'bottom'"
                  [minWidth]="220"
                  [maxWidth]="360" />
              </div>
            }
            <div class="flex items-center gap-2">
              <span class="text-text-tertiary text-xs"
                >{{ 'common.created' | translate }}:
              </span>
              <span class="text-text-primary text-xs">
                {{ metadata.createdAt | date: 'short' : undefined : currentLang() }}
              </span>
            </div>
            @if (metadata && metadata.parameter && metadata.parameter.id) {
              <div class="flex items-center gap-2">
                <span class="text-text-tertiary text-xs"
                  >{{ 'features.plan.parameters' | translate }}:
                </span>
                <span class="text-text-primary text-xs">
                  {{ metadata.parameter.name }}
                </span>
              </div>
            }

            @if (metadata && metadata.recipe?.id) {
              <div class="flex items-center gap-2">
                <span class="text-text-tertiary text-xs"
                  >{{ 'features.plan.recipe' | translate }}:
                </span>
                <span class="text-text-primary text-xs">
                  {{ metadata.recipe?.name }}
                </span>
              </div>
            }
            @for (parameter of recipeParameterEntries(); track parameter.name) {
              <div class="flex items-center gap-2">
                <span class="text-text-tertiary text-xs">{{ parameter.label }}: </span>
                <span class="text-text-primary text-xs">{{ parameter.value }}</span>
              </div>
            }
            @if (metadata && metadata.solver.id) {
              <div class="flex items-center gap-2">
                <span class="text-text-tertiary text-xs"
                  >{{ 'features.plan.solver' | translate }}:
                </span>
                <span class="text-text-primary text-xs">
                  {{ metadata.solver.name }}
                </span>
              </div>
            }
          </div>
          @if (metadata.parentPlanId) {
            <div class="flex items-center gap-2">
              <span class="text-text-tertiary text-xs"
                >{{ 'features.plan.parent_plan' | translate }}:
              </span>
              <a
                [routerLink]="makePlanRouterLink(metadata.parentPlanId)"
                class="inline-flex cursor-pointer items-center gap-1 text-xs text-zinc-900 transition-colors duration-200 hover:text-amber-600 dark:text-white dark:hover:text-amber-400"
                [attr.role]="'button'"
                [attr.tabindex]="0">
                <span class="max-w-12 truncate">{{ metadata.parentPlanId }}</span>
                <i [class]="ICONS.arrowSquareOut" class="text-xs opacity-60"></i>
              </a>
            </div>
          }
        </div>

        <!-- Children Info Table -->
        @if (metadata.childrenInfo && metadata.childrenInfo.length !== 0) {
          <div class="border-default bg-elevated flex flex-2 flex-col rounded border p-3">
            <div class="mb-3 flex items-baseline justify-between gap-2">
              <div class="flex items-baseline gap-2">
                <h3 class="text-text-primary text-sm font-semibold">
                  {{ 'features.plan.jobs.title' | translate }}
                </h3>
                <span class="text-text-tertiary font-mono text-xs">{{
                  metadata.childrenInfo.length
                }}</span>
              </div>
              <div class="inline-flex gap-1">
                @if (childrenSuccessCount() > 0) {
                  <span
                    class="inline-flex items-center gap-1 rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-semibold text-green-600 dark:bg-green-500/15 dark:text-green-400">
                    <span class="h-1.5 w-1.5 rounded-full bg-green-500"></span>
                    {{ childrenSuccessCount() }}
                  </span>
                }
                @if (childrenPendingCount() > 0) {
                  <span
                    class="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-xs font-semibold text-blue-600 dark:bg-blue-500/15 dark:text-blue-400">
                    <span class="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500"></span>
                    {{ childrenPendingCount() }}
                  </span>
                }
                @if (childrenErrorCount() > 0) {
                  <span
                    class="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-semibold text-red-600 dark:bg-red-500/15 dark:text-red-400">
                    <span class="h-1.5 w-1.5 rounded-full bg-red-500"></span>
                    {{ childrenErrorCount() }}
                  </span>
                }
              </div>
            </div>
            <div class="max-h-40 overflow-y-auto md:max-h-52">
              <table class="w-full border-collapse text-xs">
                <thead>
                  <tr class="border-weak border-b">
                    <th
                      class="text-text-secondary w-7 px-1.5 py-1.5 text-left font-medium tracking-wider uppercase">
                      #
                    </th>
                    <th
                      class="text-text-secondary px-1.5 py-1.5 text-left font-medium tracking-wider uppercase">
                      {{ 'common.status' | translate }}
                    </th>
                    <th
                      class="text-text-secondary w-16 px-1.5 py-1.5 text-right font-medium tracking-wider uppercase">
                      {{ 'features.plan.jobs.table.actions.header' | translate }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  @for (child of metadata.childrenInfoVisu; track trackChildPlan(child)) {
                    <tr class="border-weak hover:bg-interactive border-b transition-colors">
                      <td class="text-text-secondary px-1.5 py-2 font-mono">
                        {{ child.jobVisuId }}
                      </td>
                      <td class="px-1.5 py-2">
                        <app-optimization-job-status [status]="child.jobStatus" />
                      </td>
                      <td class="px-1.5 py-2 text-right">
                        <div class="inline-flex items-center justify-end gap-1">
                          @if (canCancelJob(child)) {
                            <app-button
                              type="danger"
                              size="sm"
                              [icon]="ICONS.prohibit"
                              [disabled]="cancelInFlightJobIds().has(child.jobId)"
                              [tooltip]="'features.plan.jobs.cancel.button' | translate"
                              (clickEvent)="requestCancelJob(child)" />
                          }
                          @if (canDeleteJob(child)) {
                            <app-button
                              type="danger"
                              size="sm"
                              [icon]="ICONS.trash"
                              [disabled]="deleteInFlightJobIds().has(child.jobId)"
                              [tooltip]="'features.plan.jobs.delete.button' | translate"
                              (clickEvent)="requestDeleteJob(child)" />
                          }
                          @if (canViewJobLogs() && hasJobStarted(child)) {
                            <app-button
                              type="cancel"
                              [icon]="ICONS.scroll"
                              [tooltip]="'features.plan.jobs.logs.viewButton' | translate"
                              (clickEvent)="openJobLogs(child)" />
                          }
                          @if (canViewJobArtifacts() && hasJobStarted(child)) {
                            <app-optimization-artifacts-dropdown [jobId]="child.jobId" />
                          }
                          @if (child.childPlanId) {
                            <app-button
                              type="cancel"
                              [routerLink]="makePlanRouterLink(child.childPlanId)"
                              [icon]="ICONS.arrowSquareOut"
                              [label]="
                                'features.plan.jobs.table.actions.button' | translate
                              " />
                          } @else if (isJobFinished(child)) {
                            <span class="text-text-tertiary"
                              >({{ 'common.deleted' | translate }})</span
                            >
                          } @else {
                            <span class="text-text-tertiary">—</span>
                          }
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        }
      </div>
    }

    <div class="grid min-h-0 flex-1 grid-cols-2 gap-3 overflow-hidden">
      <div
        class="border-default bg-elevated flex flex-col gap-3 overflow-y-auto rounded border p-3">
        <div class="flex items-center gap-2">
          <p class="text-text-primary font-semibold">KPIs</p>
          @if (kpisMode() !== 'stored') {
            <span
              data-testid="kpis-mode"
              class="bg-warning-soft text-warning rounded-full px-2 py-0.5 text-xs font-semibold">
              {{
                (kpisMode() === 'saved-plan'
                  ? 'features.plan.kpis.saved_plan'
                  : 'features.plan.kpis.live_label'
                ) | translate
              }}
            </span>
          }
          @if (kpisMode() === 'provisional') {
            <span class="text-text-tertiary text-xs">
              {{ 'features.plan.kpis.provisional' | translate }}
            </span>
          }
        </div>

        <div
          class="flex flex-1 flex-wrap content-start gap-3"
          [class.opacity-60]="kpisMuted()"
          cdkDropList
          cdkDropListOrientation="mixed"
          (cdkDropListDropped)="onKpiDrop($event)">
          @if (kpis().length === 0) {
            <div
              class="bg-surface border-default hover:bg-interactive duration-base flex-1 rounded border p-2 shadow-none transition-colors">
              <div class="flex items-center space-x-3">
                <div class="min-w-0 flex-1">
                  <p class="text-text-secondary truncate text-sm">
                    {{ 'kpis.no_data' | translate }}
                  </p>
                </div>
              </div>
            </div>
          }
          <!-- Compact Revenue Card -->
          @for (kpi of kpis(); track kpi.id) {
            <div
              #kpiCard
              cdkDrag
              data-testid="kpi-card"
              class="group bg-surface border-default hover:bg-interactive duration-base relative flex-1 rounded border p-2 shadow-none transition-colors"
              [class]="hasKpiDetail(kpi) ? 'cursor-pointer' : 'cursor-move'"
              [attr.role]="hasKpiDetail(kpi) ? 'button' : null"
              [attr.tabindex]="hasKpiDetail(kpi) ? 0 : null"
              [attr.aria-haspopup]="hasKpiDetail(kpi) ? 'dialog' : null"
              (click)="toggleKpiDetail(kpi, kpiCard)"
              (keydown.enter)="onKpiCardKeydown(kpi, $event)"
              (keydown.space)="onKpiCardKeydown(kpi, $event)">
              <button
                type="button"
                data-testid="kpi-hide"
                class="text-text-tertiary hover:text-text-primary absolute top-1 right-1 z-10 hidden rounded p-0.5 group-hover:block"
                [attr.aria-label]="'features.plan.kpi_hide' | translate"
                (click)="hideKpi(kpi.id, $event)">
                <i [class]="ICONS.eyeSlash" class="text-sm"></i>
              </button>
              <div class="flex items-center space-x-3">
                <div class="p-1 text-lg">
                  <i [class]="kpi.metadata.icon"></i>
                </div>
                <div class="min-w-0 flex-1">
                  @if (kpi.isPartOfTotalMapType()) {
                    <!-- nb_routes_per_vehicle: part/total rows, capped at KPI_PREVIEW_MAX -->
                    <div class="space-y-1">
                      @for (
                        entry of kpi.getPartOfTotalEntries().slice(0, KPI_PREVIEW_MAX);
                        track entry.key
                      ) {
                        <div class="flex items-center justify-between">
                          <span class="text-text-tertiary mr-2 truncate text-xs">{{
                            entry.key
                          }}</span>
                          <span class="text-text-primary text-sm font-semibold"
                            >{{ entry.part }}/{{ entry.total }}</span
                          >
                        </div>
                      }
                      @if (kpi.getPartOfTotalEntries().length > KPI_PREVIEW_MAX) {
                        <span class="text-text-tertiary cursor-default text-xs">
                          +{{ kpi.getPartOfTotalEntries().length - KPI_PREVIEW_MAX }}
                          more…
                        </span>
                      }
                    </div>
                    <p class="text-text-tertiary mt-1 truncate text-xs">
                      {{ kpi.i18nKey | translate }}
                    </p>
                  } @else if (kpi.isMapType()) {
                    <!-- Map KPI: show first KPI_PREVIEW_MAX entries, rest in the detail card -->
                    <div class="space-y-1">
                      @for (
                        entry of kpi.getMapEntries().slice(0, KPI_PREVIEW_MAX);
                        track entry.key
                      ) {
                        <div class="flex items-center justify-between">
                          <span class="text-text-tertiary mr-2 truncate text-xs">{{
                            entry.key
                          }}</span>
                          <span class="text-text-primary text-sm font-semibold">
                            {{ kpi.metadata.prefix }}{{ entry.value
                            }}{{ kpi.metadata.suffix }}
                          </span>
                        </div>
                      }
                      @if (kpi.getMapEntries().length > KPI_PREVIEW_MAX) {
                        <span class="text-text-tertiary cursor-default text-xs">
                          +{{ kpi.getMapEntries().length - KPI_PREVIEW_MAX }} more…
                        </span>
                      }
                    </div>
                    <p class="text-text-tertiary mt-1 truncate text-xs">
                      {{ kpi.i18nKey | translate }}
                    </p>
                  } @else if (kpi.isArrayType()) {
                    <!-- Array KPI: show count, full list in the detail card -->
                    <p class="text-text-primary truncate text-lg font-semibold">
                      {{ kpi.getformattedArray().length }}
                    </p>
                    <p class="text-text-tertiary truncate text-xs">
                      {{ kpi.i18nKey | translate }}
                    </p>
                  } @else {
                    <!-- Regular KPI: unchanged -->
                    <p class="text-text-primary truncate text-lg font-semibold">
                      {{ kpi.metadata.prefix }}{{ kpi.value }}{{ kpi.metadata.suffix }}
                    </p>
                    <p class="text-text-tertiary truncate text-xs">
                      {{ kpi.i18nKey | translate }}
                    </p>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      </div>
      <!-- Entities -->
      <div
        class="border-default bg-elevated flex flex-wrap gap-3 overflow-y-auto rounded border p-3">
        <p class="text-text-primary w-full font-semibold">
          {{ 'features.plan.entities' | translate }}
        </p>
        @if (isEmpty()) {
          <div>
            <p class="text-text-tertiary text-center">
              {{ 'common.no_data' | translate }}
            </p>
          </div>
        } @else {
          <app-cards-list
            class="h-full w-full"
            [config]="cardListConfig()"></app-cards-list>
        }
      </div>
    </div>
  </div>

  @if (addDataModalCfg; as config) {
    <app-modal
      [config]="addDataModalConfig"
      [size]="ModalSize.FULL"
      (closeModal)="closeEntityModal()">
      <app-entity
        modal-body
        class="flex h-full w-full"
        [overrideEntityType]="config.entityName"
        [showPlanToolbox]="true"
        [contextType]="'edition'"
        (dataAdded)="onDataAdded($event)"
        (dataAddedByFilter)="onDataAddedByFilter($event)" />
    </app-modal>
  }

  @if (showCancelJobModal()) {
    <app-modal
      [title]="'features.plan.jobs.cancel.confirmTitle' | translate"
      [config]="cancelJobModalConfig()"
      (closeModal)="closeCancelJobModal()">
      <p modal-body class="text-text-secondary text-sm">
        {{ 'features.plan.jobs.cancel.confirmMessage' | translate }}
      </p>
    </app-modal>
  }

  @if (showDeleteJobModal()) {
    <app-modal
      [title]="'features.plan.jobs.delete.confirmTitle' | translate"
      [config]="deleteJobModalConfig()"
      (closeModal)="closeDeleteJobModal()">
      <p modal-body class="text-text-secondary text-sm">
        {{ 'features.plan.jobs.delete.confirmMessage' | translate }}
      </p>
    </app-modal>
  }

  @if (showLogsModal()) {
    @if (selectedLogsJobId(); as jobId) {
      <app-modal
        [title]="'features.plan.jobs.logs.title' | translate"
        [size]="ModalSize.FULL"
        [config]="{ dismissible: true, closeOnBackdropClick: true }"
        (closeModal)="closeJobLogs()">
        <app-optimization-job-logs modal-body [optimizationId]="jobId" />
      </app-modal>
    }
  }

  <app-duplicate-plan-flow
    [(open)]="duplicateFlowOpen"
    [planId]="planMetadata()?.id ?? ''"
    [sourceName]="planMetadata()?.name ?? ''"
    [sourceDescription]="planDescription()"
    [redirectOnComplete]="true"
    (duplicated)="onPlanDuplicated($event)" />
</div>
